import React from "react";
import { Factory, ArrowRight, Boxes, FileText, ShieldCheck } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { CONTAINER } from "../../lib/data";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const ContainerShowcase = () => {
  const { t } = useLang();
  return (
    <section id="industrial" className="relative z-10 py-24 md:py-32 border-t border-white/10 bg-charcoal" data-testid="container-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black">
            <img
              src={CONTAINER.img}
              alt="Gesung container-scale power storage system"
              className="absolute inset-0 h-full w-full object-cover opacity-70"
              data-testid="container-image"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/20" />
            <div className="relative z-10 max-w-2xl p-8 md:p-16">
              <span className="inline-flex items-center gap-2 rounded-full border border-lime2/40 bg-lime2/10 px-3 py-1 text-[11px] font-mono uppercase tracking-[0.2em] text-lime2">
                <Factory size={12} /> {t.container.kicker}
              </span>
              <span className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1 text-[11px] font-mono text-white/70">
                <Boxes size={12} /> {t.container.badge}
              </span>
              <h2 className="mt-6 font-display font-extrabold tracking-tighter text-3xl md:text-5xl leading-[0.95] text-white">
                {t.container.title}
              </h2>
              <p className="mt-5 text-white/70 max-w-lg">{t.container.sub}</p>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {CONTAINER.highlights.map((h) => (
                  <div key={h.label} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-3 backdrop-blur-sm">
                    <p className="font-display text-lg font-bold text-lime2">{h.rated}</p>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-white/45 mt-1">{h.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => scrollTo("contact")}
                  data-testid="container-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-lime2 px-6 py-3 text-sm font-semibold text-black hover:brightness-110 transition"
                >
                  <Factory size={15} /> {t.container.cta} <ArrowRight size={14} />
                </button>
                <a
                  href={CONTAINER.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="container-datasheet"
                  className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-lime2 transition-colors"
                >
                  <FileText size={15} /> {t.container.datasheet}
                </a>
              </div>
              <p className="mt-3 text-sm font-medium text-lime2/90">{t.container.price}</p>
              <p className="mt-2 inline-flex items-center gap-2 text-sm text-white/70">
                <ShieldCheck size={15} className="text-lime2" /> {t.catalog.warrantyGen}
              </p>
            </div>
          </div>

          {/* Model matrix */}
          <div className="mt-8" data-testid="container-spec-table">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">{t.container.specTitle}</p>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="bg-white/[0.03] text-left text-white/50">
                    <th className="px-5 py-3.5 font-medium">{t.container.cols.model}</th>
                    <th className="px-5 py-3.5 font-medium">{t.container.cols.rated}</th>
                    <th className="px-5 py-3.5 font-medium">{t.container.cols.peak}</th>
                    <th className="px-5 py-3.5 font-medium">{t.container.cols.battery}</th>
                    <th className="px-5 py-3.5 font-medium">{t.container.cols.size}</th>
                  </tr>
                </thead>
                <tbody>
                  {CONTAINER.models.map((m, i) => (
                    <tr key={m.model} className={`border-t border-white/5 ${i % 2 ? "bg-white/[0.02]" : ""}`} data-testid={`container-row-${m.model}`}>
                      <td className="px-5 py-3.5 font-display font-bold text-white">Gesung {m.model}</td>
                      <td className="px-5 py-3.5 text-lime2 font-medium">{m.ratedKw} kW</td>
                      <td className="px-5 py-3.5 text-white/80">{m.peakKw} kW</td>
                      <td className="px-5 py-3.5 text-white/80">{m.kwh} kWh</td>
                      <td className="px-5 py-3.5 text-white/60">{m.container}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
